// Weighted split-test router for the main funnel URL (/vsl-v3).
// Assigns the visitor a variant server-side (which also records the page_view),
// pins it for the session, then renders that variant's landing page with its
// own tracking disabled so the view is only counted once.
import { useEffect, useState } from "react";
import {
  getVisitorId,
  isInternalTraffic,
  SESSION_ID_KEY,
  VARIANT_ID_KEY,
} from "@/lib/analytics";
import VariantA from "@/components/funnel/variants/VariantA";
import VariantB from "@/components/funnel/variants/VariantB";
import VariantC from "@/components/funnel/variants/VariantC";
import VariantD from "@/components/funnel/variants/VariantD";

type Label = "A" | "B" | "C" | "D";

const PAGES: Record<Label, (p: { track?: boolean }) => React.ReactElement> = {
  A: VariantA,
  B: VariantB,
  C: VariantC,
  D: VariantD,
};

export function VariantRouter({ path = "/vsl-v3" }: { path?: string }) {
  const [label, setLabel] = useState<Label>("A");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const sessionId =
          sessionStorage.getItem(SESSION_ID_KEY) ?? crypto.randomUUID();
        sessionStorage.setItem(SESSION_ID_KEY, sessionId);
        const existing = sessionStorage.getItem(VARIANT_ID_KEY);
        const { assignVariantAndTrackView } = await import(
          "@/lib/variant-assign.functions"
        );
        const res = await assignVariantAndTrackView({
          data: {
            session_id: sessionId,
            visitor_id: getVisitorId(),
            existing_variant_id: existing,
            path,
            preview: isInternalTraffic(),
          },
        });
        if (cancelled) return;
        if (res.variant_id) {
          sessionStorage.setItem(VARIANT_ID_KEY, res.variant_id);
          sessionStorage.setItem("inflow_variant_label", res.variant_label);
        }
        setLabel(res.variant_label as Label);
      } catch (e) {
        console.error("[lp] variant assignment failed", e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [path]);

  const Page = PAGES[label] ?? VariantA;
  return <Page track={false} />;
}
